import { GridCellParams, GridColDef, GridValidRowModel } from "@mui/x-data-grid-premium";
import { accountFormat, isNegative, isNegativeFormatted } from "@/Common/helpers/number";
import { Nullable } from "@/Common/interfaces/types";
import { ChargebacksRecord, ChargebacksWrapper, ClassNameHints, Condition, SetConditionalClassName } from "@/Washouts/types/commons";
import { FOOTER_PARAM_ID, TABLE_TOTAL_MARKER } from "@/Washouts/constants";
import { Issue } from "@/Common/types/tables";
import { isNumber } from "lodash";

export function isCellIssued(
	_id: string,
	field: Nullable<string>,
	reference: Nullable<string>,
	issues: Array<Issue> = []
): Nullable<string> {
	if (issues?.length) {
		for (const issue of issues) {
			if (issue && issue.tableReference === reference && issue.rowReference === _id && issue.columnReference === field) {
				return issue.color;
			}
		}
	}
	return null;
}

const MONEY_KEY_WORDS = ["gross", "commission", "profit", "compensation", "pvr", "invoice"];

export function isMoneyRelated(field: string): boolean {
	return MONEY_KEY_WORDS.some((word) => field.toLowerCase().includes(word));
}

export function isFooterCell(params: Nullable<GridCellParams<GridValidRowModel>>): boolean {
	return params?.id === FOOTER_PARAM_ID;
}

export function computeProps(
	reference: Nullable<string>,
	params: Nullable<GridCellParams<GridValidRowModel>>,
	issues: Issue[] = []
): ClassNameHints {
	const colorIssued = isCellIssued(params?.row._id, params?.field, reference, issues);
	const isTotal = Boolean(params?.id?.toString().includes(TABLE_TOTAL_MARKER));
	const isFooter = isFooterCell(params);
	const isNumber = params?.colDef.type === "number" || typeof params?.value === "number";
	return {
		colorIssued,
		isTotal,
		isFooter,
		isNegative: isNumber && Math.sign(Number(params?.value)) === -1,
		isFunded: (params?.row?.funded || false) && !isTotal,
		noFocus: isFooter || !colorIssued,
		isMoney: isNumber && (isMoneyRelated(params?.field) || isMoneyRelated(params?.colDef.headerName ?? "")),
		noWrap: false,
		isPercentage: false,
	};
}

export type FormatterOptions = {
	decimals?: number;
	wrapper?: "parentheses";
};

export function applyFormatter(
	params: Nullable<GridCellParams<GridValidRowModel>>,
	formatter: "account" | "absolute",
	options?: FormatterOptions
) {
	if (params?.value) {
		switch (formatter) {
			case "account":
				return accountFormat(params.value, options?.decimals ?? 1);
			case "absolute":
				return getAbsoluteValue(params.value, options);
			default:
				return params.value;
		}
	}
	return null;
}

export function getAbsoluteValue(value: unknown, formatOptions?: FormatterOptions): string {
	const numVal = isNaN(Number(value)) ? 0 : Number(value);
	const showValue = formatOptions ? accountFormat(Math.abs(numVal), formatOptions.decimals) : accountFormat(Math.abs(numVal));
	return Math.sign(numVal) === -1 ? `(${showValue})` : showValue;
}

export function getDealStatusClassName(status: string): string {
	switch (status.toUpperCase()) {
		case "CLOSED":
		case "C":
			return "text-[#2357BE]";
		case "DELIVERED":
		case "D":
			return "text-[#FFB507]";
		case "FINALIZED":
		case "F":
			return "text-[#009245]";
		case "BACKOUT":
		case "B":
			return "text-[#DE4759]";
		default:
			return "";
	}
}

export function prepareChargebacksData(preOwnedChargebacks?: ChargebacksWrapper, newChargebacks?: ChargebacksWrapper) {
	const chargebacks: { total: number; chargebacks: ChargebacksRecord[] } = {
		total: 0,
		chargebacks: [],
	};
	chargebacks.total += preOwnedChargebacks?.chargebackCommission ?? 0;
	chargebacks.total += newChargebacks?.chargebackCommission ?? 0;
	for (let i = 0; i < (preOwnedChargebacks?.chargebacks?.length ?? 0); i++) {
		if (preOwnedChargebacks) {
			chargebacks.chargebacks.push(preOwnedChargebacks?.chargebacks[i]);
		}
	}
	for (let i = 0; i < (newChargebacks?.chargebacks?.length ?? 0); i++) {
		if (newChargebacks) {
			chargebacks.chargebacks.push(newChargebacks?.chargebacks[i]);
		}
	}
	return chargebacks;
}

export function normalizeCamelCase(str: string) {
	try {
		let words = str?.split(/(?=[A-Z])/);
		words = words?.map((word) => word.charAt(0).toUpperCase() + word.slice(1));

		return words?.join(" ") ?? str ?? "";
	} catch (error) {
		return "Keys";
	}
}

export function isSubTotalCell(row?: GridValidRowModel) {
	return row?.subFooter || row?.isFooter;
}

export function isNegativeCell(params: GridCellParams<GridValidRowModel>) {
	if (params.formattedValue) {
		if (String(params.formattedValue).startsWith("(")) {
			return true;
		}
		if (isNumber(params.formattedValue)) {
			return +Number(params.formattedValue) < 0;
		}
		return isNegative(params.formattedValue);
	}
	if (params.value) {
		return isNegative(params.value);
	}
	return false;
}

export function getCellClassNameConditions({
	set = [],
	conditions = [],
	overrideDefaults = false,
}: SetConditionalClassName<GridCellParams<GridValidRowModel>>): SetConditionalClassName<GridCellParams<GridValidRowModel>> {
	const defaultConditions: Condition<GridCellParams<GridValidRowModel>>[] = [
		{
			className: "!bg-[var(--primary-color)] !text-white",
			condition: (params) => {
				const isFooter = isFooterCell(params);
				if (!isFooter) {
					return false;
				}
				const isEmptyValue = params?.formattedValue === "-";
				if (isEmptyValue) {
					return true;
				}
				return !isNegativeCell({ ...params, formattedValue: String(params?.formattedValue).replace(",", "") });
			},
		},
		{
			className: "text-[color:--text-alert-color] before:text-[color:--text-alert-color] after:text-[color:--text-alert-color]",
			condition: (params) => isNegative(params?.value) || isNegativeFormatted(params?.formattedValue ?? params?.value),
		},
		{
			className: "!bg-[color:--light-gray-color-v2]",
			condition: (params) => isNegativeCell(params) && (isSubTotalCell(params?.row) || isFooterCell(params)),
		},
	];
	return {
		set,
		conditions: overrideDefaults ? [...conditions] : [...defaultConditions, ...conditions],
	};
}

export function getDecimalFilterFn(decimals = 2): GridColDef["getApplyQuickFilterFn"] {
	return (searchTerm) => {
		const num = Number(searchTerm);
		if (isNaN(num)) {
			return null;
		}
		return (cellValue) => {
			const val = Number(cellValue);
			if (isNaN(val)) {
				return false;
			}
			return accountFormat(val, decimals) === accountFormat(num, decimals);
		};
	};
}
